import {
    AGENT_CONFIRM_TOOL,
    AGENT_FILE_REMOVE,
    AGENT_FILE_UPLOAD,
    AGENT_FILE_UPLOAD_DONE,
    AGENT_FILE_UPLOAD_ERROR,
    AGENT_MESSAGE_SEND,
    AGENT_MESSAGE_SEND_DONE,
    AGENT_PRESETS_DONE,
    AGENT_PRESETS_ERROR,
    AGENT_PRESETS_REQUEST,
    AGENT_PRESET_SELECT,
    AGENT_SHEET_CLOSE,
    AGENT_SHEET_OPEN,
    AGENT_SHEET_SET_HEIGHT,
    AGENT_SHEET_TOGGLE_COLLAPSE,
    AGENT_STREAM_EVENT,
    AGENT_STREAM_STATUS,
    AGENT_THREAD_CREATE_DONE,
    AGENT_THREAD_DELETE_DONE,
    AGENT_THREAD_ERROR,
    AGENT_THREAD_LIST_DONE,
    AGENT_THREAD_LIST_ERROR,
    AGENT_THREAD_LIST_REQUEST,
    AGENT_THREAD_LOAD_DONE,
    AGENT_THREAD_LOAD_REQUEST,
    AGENT_THREAD_MODE_DONE,
    AGENT_THREAD_SELECT,
    AGENT_USAGE_DONE,
    AGENT_USAGE_ERROR,
    AGENT_USAGE_REQUEST,
} from '../actions/agent';
import {applyEvent, createAssistantTurn, createUserTurn, turnFromApi} from '../agent-turn-utils';
import {AGENT_SHEET_DEFAULT_HEIGHT, clampSheetHeight, loadAgentUiState} from '../agent-storage';

const IDLE_STREAM = {threadId: null, turnId: null, lastSeq: 0, status: 'idle'};

function emptyThread() {
    return {
        thread: null,
        turns: [],
        pending: null,
        limit: null,
        error: null,
        isLoading: false,
        // Files uploaded but not yet sent with a message. Cleared on send —
        // a fileId is single-use and belongs to exactly one thread.
        attachments: [],
        isUploading: false,
    };
}

function initialState() {
    const ui = loadAgentUiState();

    return {
        isOpen: ui.isOpen,
        isCollapsed: ui.isCollapsed,
        sheetHeight: ui.sheetHeight ?? AGENT_SHEET_DEFAULT_HEIGHT,
        activeThreadId: ui.activeThreadId,
        threadList: {items: [], cursor: null, isLoading: false, error: null},
        threads: {},
        stream: Object.assign({}, IDLE_STREAM),
        focusNonce: 0,
        // Errors that belong to no thread — a failed POST /v1/threads has no
        // threadId to hang off, and without this the failure is invisible.
        error: null,
        // Model presets (§10) — a developer/admin affordance, gated by
        // isAgentAdmin, not per-contact data. `items` stays cached across a
        // login switch; `selectedPreset` (which NEW thread will use) does not.
        presets: {items: null, isLoading: false, error: null},
        selectedPreset: null,
        // Usage/token-counters screen (§11) — IS per-contact data, cleared on
        // a login switch the same way threads are.
        usage: {report: null, isLoading: false, error: null},
    };
}

/** Patches an existing thread entry; a no-op for a thread we do not know. */
function patchThread(state, threadId, patch) {
    if (!state.threads[threadId]) {
        return state;
    }

    return upsertThread(state, threadId, patch);
}

/** Patches a thread entry, creating a minimal one when it is absent. */
function upsertThread(state, threadId, patch) {
    const current = state.threads[threadId] ?? emptyThread();

    return Object.assign({}, state, {
        threads: Object.assign({}, state.threads, {
            [threadId]: Object.assign({}, current, patch),
        }),
    });
}

function replaceTurn(turns, turnId, next) {
    return turns.map((turn) => (turn.turnId === turnId ? next : turn));
}

const agentReducer = (state = initialState(), action) => {
    switch (action.type) {
        case AGENT_SHEET_OPEN:
            // The nonce advances on every open request, including when the sheet
            // is already open, so the composer can be re-focused.
            return Object.assign({}, state, {isOpen: true, focusNonce: state.focusNonce + 1});

        case AGENT_SHEET_CLOSE:
            return Object.assign({}, state, {isOpen: false});

        case AGENT_SHEET_TOGGLE_COLLAPSE:
            return Object.assign({}, state, {isCollapsed: !state.isCollapsed});

        case AGENT_SHEET_SET_HEIGHT:
            return Object.assign({}, state, {
                sheetHeight: clampSheetHeight(
                    action.data.height,
                    typeof window === 'undefined' ? 1000 : window.innerHeight,
                ),
            });

        case AGENT_THREAD_SELECT:
            return Object.assign({}, state, {activeThreadId: action.data.threadId});

        case AGENT_THREAD_LIST_REQUEST:
            return Object.assign({}, state, {
                threadList: Object.assign({}, state.threadList, {isLoading: true, error: null}),
            });

        case AGENT_THREAD_LIST_DONE:
            return Object.assign({}, state, {
                threadList: {
                    items: action.data.threads ?? [],
                    cursor: action.data.cursor ?? null,
                    isLoading: false,
                    error: null,
                },
            });

        case AGENT_THREAD_LIST_ERROR:
            return Object.assign({}, state, {
                threadList: Object.assign({}, state.threadList, {
                    isLoading: false, error: action.data.error,
                }),
            });

        case AGENT_THREAD_CREATE_DONE: {
            const {threadId, thread} = action.data;

            return Object.assign({}, state, {
                activeThreadId: threadId,
                error: null,
                threads: Object.assign({}, state.threads, {
                    [threadId]: Object.assign({}, emptyThread(), {thread}),
                }),
                threadList: Object.assign({}, state.threadList, {
                    items: [thread].concat(state.threadList.items.filter((it) => it.threadId !== threadId)),
                }),
            });
        }

        case AGENT_THREAD_LOAD_REQUEST: {
            const {threadId} = action.data;
            const current = state.threads[threadId] ?? emptyThread();

            return Object.assign({}, state, {
                threads: Object.assign({}, state.threads, {
                    [threadId]: Object.assign({}, current, {isLoading: true, error: null}),
                }),
            });
        }

        case AGENT_THREAD_LOAD_DONE: {
            const threadId = action.data.thread?.threadId;
            const pending = action.data.pending ?? null;
            const current = state.threads[threadId] ?? emptyThread();
            const turns = (action.data.turns ?? []).map(turnFromApi);
            const lastTurn = turns[turns.length - 1];

            return Object.assign({}, state, {
                error: null,
                threads: Object.assign({}, state.threads, {
                    [threadId]: Object.assign({}, current, {
                        thread: action.data.thread,
                        turns,
                        pending,
                        limit: lastTurn?.limit ?? null,
                        isLoading: false,
                        error: null,
                    }),
                }),
            });
        }

        case AGENT_THREAD_ERROR: {
            const {threadId, error} = action.data;

            // A failed thread-create carries threadId: null, and an error for a
            // thread we never loaded has nowhere to land either. Both would be
            // swallowed silently, so they surface at the top level instead.
            if (!threadId || !state.threads[threadId]) {
                return Object.assign({}, state, {error});
            }

            return patchThread(state, threadId, {isLoading: false, error});
        }

        case AGENT_THREAD_DELETE_DONE: {
            const {threadId} = action.data;
            const threads = Object.assign({}, state.threads);
            delete threads[threadId];

            return Object.assign({}, state, {
                threads,
                activeThreadId: state.activeThreadId === threadId ? null : state.activeThreadId,
                threadList: Object.assign({}, state.threadList, {
                    items: state.threadList.items.filter((it) => it.threadId !== threadId),
                }),
            });
        }

        case AGENT_MESSAGE_SEND: {
            const {threadId, text} = action.data;

            if (!threadId) {
                return state;
            }

            // The thread entry can legitimately be missing — after a reload the
            // slice boots with a persisted activeThreadId and no threads at all.
            // Returning `state` here would POST the message, run it server-side,
            // and render nothing, permanently. Create the entry instead.
            const current = state.threads[threadId] ?? emptyThread();

            // Optimistic user bubble. Its id is provisional — the server never
            // returns an id for the user turn, only for the assistant turn.
            const userTurn = createUserTurn(
                `local_${current.turns.length}`,
                text,
                current.attachments,
            );

            return Object.assign({}, upsertThread(state, threadId, {
                turns: current.turns.concat([userTurn]),
                // Consumed by this message; a fileId cannot be reused.
                attachments: [],
                limit: null,
                error: null,                              // this thread's error
            }), {
                error: null,                              // and the thread-less one
            });
        }

        case AGENT_MESSAGE_SEND_DONE: {
            const {threadId, turnId, turnMode, preset} = action.data;

            if (!threadId) {
                return state;
            }

            const current = state.threads[threadId] ?? emptyThread();

            return Object.assign({}, upsertThread(state, threadId, {
                turns: current.turns.concat([createAssistantTurn(turnId, turnMode, preset)]),
            }), {
                stream: {threadId, turnId, lastSeq: 0, status: 'live'},
            });
        }

        case AGENT_THREAD_MODE_DONE: {
            const {threadId, defaultMode} = action.data;
            const current = state.threads[threadId];

            if (!current || !current.thread) {
                return state;
            }

            return patchThread(state, threadId, {
                thread: Object.assign({}, current.thread, {defaultMode}),
            });
        }

        case AGENT_CONFIRM_TOOL: {
            const {threadId, turnId} = action.data;
            const current = state.threads[threadId];

            if (!current) {
                return state;
            }

            // The decision is on its way to the server, so the card is spent.
            // Nothing else ever cleared it, which left the approval card on
            // screen and the composer dead for the rest of the thread's life.
            return patchThread(state, threadId, {
                pending: null,
                turns: current.turns.map((turn) => (
                    turn.turnId === turnId && turn.pending
                        ? Object.assign({}, turn, {pending: null})
                        : turn
                )),
            });
        }

        case AGENT_STREAM_EVENT: {
            const {threadId, turnId, event} = action.data;
            const current = state.threads[threadId];

            if (!current) {
                return state;
            }

            const turn = current.turns.find((it) => it.turnId === turnId);

            if (!turn) {
                return state;
            }

            const nextTurn = applyEvent(turn, event);

            const next = patchThread(state, threadId, {
                turns: replaceTurn(current.turns, turnId, nextTurn),
                // Lift the durable bits onto the thread so the sheet can render
                // them without hunting through turns.
                pending: nextTurn.pending
                    ? Object.assign({turnId}, nextTurn.pending)
                    : current.pending,
                limit: nextTurn.limit ?? current.limit,
            });

            if (typeof event.seq !== 'number') {
                return next;
            }

            return Object.assign({}, next, {
                stream: Object.assign({}, next.stream, {lastSeq: event.seq}),
            });
        }

        case AGENT_STREAM_STATUS: {
            const {status} = action.data;

            if (status === 'idle') {
                return Object.assign({}, state, {
                    stream: Object.assign({}, IDLE_STREAM, {threadId: state.stream.threadId}),
                });
            }

            return Object.assign({}, state, {
                stream: Object.assign({}, state.stream, {
                    status,
                    threadId: action.data.threadId ?? state.stream.threadId,
                    turnId: action.data.turnId ?? state.stream.turnId,
                    lastSeq: action.data.lastSeq ?? state.stream.lastSeq,
                }),
            });
        }

        case 'DONE_LOGIN':
            // A different contact may be signing in on this machine, and not
            // always via a LOGOUT first — the /logging-in token-swap path lands
            // here directly. Threads belong to a contact, so anything carried
            // across would load someone else's thread and 404. Sheet geometry is
            // a UI preference, not user data, so it survives — and so does the
            // cached preset list, which is global config, not per-contact.
            return Object.assign({}, state, {
                activeThreadId: null,
                threads: {},
                threadList: {items: [], cursor: null, isLoading: false, error: null},
                stream: Object.assign({}, IDLE_STREAM),
                error: null,
                selectedPreset: null,
                usage: {report: null, isLoading: false, error: null},
            });

        case AGENT_PRESETS_REQUEST:
            return Object.assign({}, state, {
                presets: Object.assign({}, state.presets, {isLoading: true, error: null}),
            });

        case AGENT_PRESETS_DONE:
            return Object.assign({}, state, {
                presets: {items: action.data.presets ?? {}, isLoading: false, error: null},
            });

        case AGENT_PRESETS_ERROR:
            return Object.assign({}, state, {
                presets: Object.assign({}, state.presets, {isLoading: false, error: action.data.error}),
            });

        case AGENT_PRESET_SELECT:
            return Object.assign({}, state, {selectedPreset: action.data.preset});

        case AGENT_USAGE_REQUEST:
            return Object.assign({}, state, {
                usage: Object.assign({}, state.usage, {isLoading: true, error: null}),
            });

        case AGENT_USAGE_DONE:
            return Object.assign({}, state, {
                usage: {report: action.data.report, isLoading: false, error: null},
            });

        case AGENT_USAGE_ERROR:
            return Object.assign({}, state, {
                usage: Object.assign({}, state.usage, {isLoading: false, error: action.data.error}),
            });

        case AGENT_FILE_UPLOAD: {
            const {threadId} = action.data;

            if (!threadId) {
                return state;
            }

            return upsertThread(state, threadId, {isUploading: true, error: null});
        }

        case AGENT_FILE_UPLOAD_DONE: {
            const {threadId, file} = action.data;
            const current = state.threads[threadId] ?? emptyThread();

            return upsertThread(state, threadId, {
                isUploading: false,
                attachments: current.attachments.concat([file]),
            });
        }

        case AGENT_FILE_UPLOAD_ERROR: {
            const {threadId, error} = action.data;

            if (!threadId) {
                return Object.assign({}, state, {error});
            }

            return upsertThread(state, threadId, {isUploading: false, error});
        }

        case AGENT_FILE_REMOVE: {
            const {threadId, fileId} = action.data;
            const current = state.threads[threadId];

            if (!current) {
                return state;
            }

            return upsertThread(state, threadId, {
                attachments: current.attachments.filter((it) => it.fileId !== fileId),
            });
        }

        default:
            return state;
    }
};

export default agentReducer;
